import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';

const CookieBanner = () => {
  const { showBanner, acceptAll, rejectAll, openSettings } = useCookieConsent();
  const { t } = useLanguage();

  useEffect(() => {
    // Prevent body scroll when banner is shown (TTDSG compliance)
    if (showBanner) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showBanner]);

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleRejectAll = () => {
    rejectAll();
  };

  const handleCustomize = () => {
    openSettings();
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div className="st-cookie-banner-backdrop" aria-hidden="true" />
      
      {/* Cookie Banner */}
      <div 
        className="st-cookie-banner" 
        role="dialog" 
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
      >
        <div className="st-cookie-banner-container">
          {/* Header with Icon */}
          <div className="st-cookie-banner-header">
            <div className="st-cookie-icon-wrapper">
              <Icon icon="mdi:cookie" className="st-cookie-icon" />
            </div>
            <h2 id="cookie-banner-title" className="st-cookie-banner-title">
              {t('cookies.banner.title')}
            </h2>
          </div>

          {/* Description */}
          <p id="cookie-banner-description" className="st-cookie-banner-description">
            {t('cookies.banner.description')}{' '}
            <Link to="/cookie-policy" className="st-cookie-banner-link">
              {t('cookies.banner.learnMore')}
            </Link>
          </p>

          {/* Cookie Categories Preview */}
          <div className="st-cookie-banner-categories">
            <div className="st-cookie-category-preview">
              <Icon icon="mdi:shield-check" className="st-category-icon st-category-icon-essential" />
              <span className="st-category-text">
                {t('cookies.banner.essentialShort')}
              </span>
            </div>
            <div className="st-cookie-category-preview">
              <Icon icon="mdi:chart-line" className="st-category-icon st-category-icon-analytics" />
              <span className="st-category-text">
                {t('cookies.banner.analyticsShort')}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="st-cookie-banner-actions">
            <button 
              onClick={handleAcceptAll}
              className="st-cookie-btn st-cookie-btn-accept"
              aria-label={t('cookies.banner.acceptAll')}
            >
              <Icon icon="mdi:check-circle" />
              {t('cookies.banner.acceptAll')}
            </button>
            
            <button 
              onClick={handleCustomize}
              className="st-cookie-btn st-cookie-btn-customize"
              aria-label={t('cookies.banner.customize')}
            >
              <Icon icon="mdi:tune" />
              {t('cookies.banner.customize')}
            </button>
            
            <button 
              onClick={handleRejectAll}
              className="st-cookie-btn st-cookie-btn-reject"
              aria-label={t('cookies.banner.rejectAll')}
            >
              <Icon icon="mdi:close-circle" />
              {t('cookies.banner.rejectAll')}
            </button>
          </div>

          {/* Footer Note */}
          <div className="st-cookie-banner-footer">
            <Icon icon="mdi:information" className="st-info-icon" />
            <span className="st-cookie-banner-note">
              {t('cookies.banner.footerNote')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
